import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    CollapsibleCard,
    Modal,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import type {
    AuthorizedMedicationInfoFragment,
    CoverageFlags,
    DigitallyReceivedPrescriberOrderInfoFragment,
    MedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import { PrescriberOrderDetails } from '../../../routes/orders/details/PrescriberOrderDetails';
import { NotCoveredModal } from '../../common/drug-coverage-alerts/NotCoveredModal';
import { PARequiredModal } from '../../common/drug-coverage-alerts/PARequiredModal';
import { MissingBenefitsCard } from '../../prior-authorization/components/MissingBenefitsCard';

import { DAWCardFooter } from './DAWCardFooter';
import { GenericSwapCardFooter } from './GenericSwapCardFooter';
import { PatientProductDetailsHeader } from './PatientProductDetailsHeader';

export type PatientProductDetailsV2Props = {
    patientName?: string;
    productDescription?: string;
    quantity?: number | null;
    prescriberOrder?: DigitallyReceivedPrescriberOrderInfoFragment;
    authorizedMedicationSavings: number;
    authorizedMed: AuthorizedMedicationInfoFragment;
    prescribedMed: MedicationInfoFragment | UnknownMedicationInfoFragment;
    setOverrideOfferList: (
        override: 'authorized' | 'prescribed' | 'default'
    ) => void;
    showAuthorizedOffersChecked: boolean;
    coverageFlags?: CoverageFlags;
};

export const PatientProductDetailsV2 = ({
    patientName,
    productDescription,
    quantity,
    prescriberOrder,
    authorizedMedicationSavings,
    authorizedMed,
    prescribedMed,
    setOverrideOfferList,
    showAuthorizedOffersChecked,
    coverageFlags,
}: PatientProductDetailsV2Props) => {
    const { t } = useTranslation();
    const [
        isGenericModalOpen,
        { close: closeGenericModal, toggle: toggleGenericModal },
    ] = useDisclosure(false);
    const [isPARequiredModalOpen, { toggle: togglePARequiredModal }] =
        useDisclosure(false);
    const [isNotCoveredModalOpen, { toggle: toggleNotCoveredModal }] =
        useDisclosure(false);

    const isSwapped =
        authorizedMed.id !== prescribedMed.id &&
        authorizedMedicationSavings > 0;
    const footerColor = showAuthorizedOffersChecked ? 'saving' : 'gray';

    if (!prescriberOrder) {
        return <MissingBenefitsCard />;
    }

    // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
    const toggleCheckbox = useCallback(() => {
        if (showAuthorizedOffersChecked) {
            setOverrideOfferList('prescribed');
        } else {
            setOverrideOfferList('authorized');
        }
    }, [showAuthorizedOffersChecked, setOverrideOfferList]);

    const renderCardFooter = () => {
        if (prescriberOrder.dispenseAsWritten) {
            return <DAWCardFooter toggleGenericModal={toggleGenericModal} />;
        }

        if (isSwapped) {
            return (
                <GenericSwapCardFooter
                    authorizedMedication={authorizedMed}
                    authorizedMedicationSavings={authorizedMedicationSavings}
                    footerColor={footerColor}
                    showAuthorizedOffersChecked={showAuthorizedOffersChecked}
                    toggleCheckbox={toggleCheckbox}
                    toggleGenericModal={toggleGenericModal}
                />
            );
        }

        return null;
    };

    const CardFooter = renderCardFooter();

    const CardHeader = (
        <PatientProductDetailsHeader
            patientName={patientName}
            productDescription={productDescription}
            quantity={quantity}
            prescriberOrder={prescriberOrder}
            coverageFlags={coverageFlags}
            togglePARequiredModal={togglePARequiredModal}
            toggleNotCoveredModal={toggleNotCoveredModal}
        />
    );

    return (
        <>
            <CollapsibleCard
                variant="elevated"
                header={CardHeader}
                footer={CardFooter}
                fullWidthFooter
            >
                <PrescriberOrderDetails prescriberOrder={prescriberOrder} />
            </CollapsibleCard>
            <Modal
                opened={isGenericModalOpen}
                onClose={closeGenericModal}
                title={t('drx.product.details.modals.generic.title')}
            >
                <Body1>{t('drx.product.details.modals.generic.body')}</Body1>
            </Modal>
            <PARequiredModal
                opened={isPARequiredModalOpen}
                close={togglePARequiredModal}
            />
            <NotCoveredModal
                opened={isNotCoveredModalOpen}
                close={toggleNotCoveredModal}
            />
        </>
    );
};
